import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { canUsePaymentAccount, paymentAccountLabel, merchantClearingHint } from "../lib/paymentAccounts";
import { useInvoicePilot } from "./InvoicePilotLink";
import { useOrg } from "../lib/org";
import { useNavigate } from 'react-router-dom';
import { useCorrections, useCorrectionStatus } from './Corrections';
import {
  Button,
  ErrorState,
  Field,
  Modal,
  Money,
  PageHeader,
  Select,
  Spinner,
  TextField,
} from "../components/ui";
import { DataTable } from "../components/DataTable";
import { dollarsToCents, formatDate, today } from "../lib/format";
import type {
  Account,
  BillListRow,
  Customer,
  InvoiceListRow,
  Payment,
  Vendor,
} from "../lib/models";

export default function Payments() {
  const corrections=useCorrections(),statuses=useCorrectionStatus(),navigate=useNavigate();
  const { currentOrg } = useOrg();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const currency = currentOrg?.baseCurrency ?? "USD";

  const { data, isLoading, error } = useQuery({
    queryKey: ["payments", currentOrg?.id],
    queryFn: () => api.get<Payment[]>("/payments"),
    enabled: !!currentOrg,
  });

  return (
    <div>
      <PageHeader
        title="Payments"
        subtitle="Money received from customers and paid to vendors"
        action={<Button onClick={() => setOpen(true)}>Record payment</Button>}
      />
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <DataTable
          rows={data}
          rowKey={(r) => r.id}
          columns={[
            { key: "date", header: "Date", render: (r) => formatDate(r.txnDate) },
            {
              key: "dir",
              header: "Type",
              render: (r) => (
                <span className="capitalize">{r.direction}{statuses.data?.reversed.some(x=>x.target_id===r.id)?' · Reversed (original record)':statuses.data?.refunded.some(x=>x.payment_id===r.id)?' · Refund recorded':''}</span>
              ),
            },
            { key: "method", header: "Method", render: (r) => r.method ?? "—" },
            { key: "ref", header: "Reference", render: (r) => r.reference ?? "—" },
            ...(corrections.data?.enabled?[{key:'correct',header:'Correction',render:(r:Payment)=><Button variant="secondary" onClick={()=>navigate(`/corrections?action=move_payment&target=${encodeURIComponent(r.id)}`)}>Review / correct</Button>}]:[]),
            {
              key: "amount",
              header: "Amount",
              align: "right",
              render: (r) => <Money cents={r.amount} currency={currency} />,
            },
            {
              key: "unapplied",
              header: "Unapplied",
              align: "right",
              render: (r) => <Money cents={r.unapplied} currency={currency} />,
            },
          ]}
        />
      )}

      <PaymentModal
        open={open}
        onClose={() => setOpen(false)}
        onSaved={() => {
          setOpen(false);
          qc.invalidateQueries({ queryKey: ["payments"] });
          qc.invalidateQueries({ queryKey: ["invoices"] });
          qc.invalidateQueries({ queryKey: ["bills"] });
        }}
      />
    </div>
  );
}

export function PaymentModal({
  open,
  onClose,
  onSaved,
  invoice,
  bill,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  invoice?: { id: string; customerId: string; docNumber: string | null };
  bill?: { id: string; vendorId: string; docNumber: string | null };
}) {
  const { currentOrg } = useOrg();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const pilot = useInvoicePilot();

  const [direction, setDirection] = useState<"received" | "made">(bill ? "made" : "received");
  const [partyId, setPartyId] = useState(invoice?.customerId ?? bill?.vendorId ?? "");
  const [depositAccountId, setDepositAccountId] = useState("");
  const [txnDate, setTxnDate] = useState(today());
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [applied, setApplied] = useState<Record<string, string>>({});

  const { data: customers } = useQuery({
    queryKey: ["customers", currentOrg?.id],
    queryFn: () => api.get<Customer[]>("/customers"),
    enabled: open && !!currentOrg,
  });
  const { data: vendors } = useQuery({
    queryKey: ["vendors", currentOrg?.id],
    queryFn: () => api.get<Vendor[]>("/vendors"),
    enabled: open && !!currentOrg,
  });
  const { data: accounts } = useQuery({
    queryKey: ["accounts", currentOrg?.id],
    queryFn: () => api.get<Account[]>("/accounts"),
    enabled: open && !!currentOrg,
  });
  const { data: invoices } = useQuery({
    queryKey: ["invoices", currentOrg?.id],
    queryFn: () => api.get<InvoiceListRow[]>("/invoices"),
    enabled: open && direction === "received" && !!currentOrg,
  });
  const { data: bills } = useQuery({
    queryKey: ["bills", currentOrg?.id],
    queryFn: () => api.get<BillListRow[]>("/bills"),
    enabled: open && direction === "made" && !!currentOrg,
  });

  const bankAccounts = (accounts ?? []).filter(
    (a) => pilot.data?.enabled ? canUsePaymentAccount(a, direction, currency) : a.type === "asset" || a.type === "liability",
  );

  const openDocs = useMemo(() => {
    if (direction === "received") {
      return (invoices ?? [])
        .filter((i) => i.customerId === partyId && i.balance > 0 && (!invoice || i.id === invoice.id))
        .map((i) => ({ id: i.id, label: i.docNumber ?? i.id, balance: i.balance, key: "invoiceId" as const }));
    }
    return (bills ?? [])
      .filter((b) => b.vendorId === partyId && b.balance > 0 && (!bill || b.id === bill.id))
      .map((b) => ({ id: b.id, label: b.docNumber ?? b.id, balance: b.balance, key: "billId" as const }));
  }, [direction, invoices, bills, partyId, invoice, bill]);

  const appliedTotal = Object.values(applied).reduce(
    (s, v) => s + dollarsToCents(v || "0"),
    0,
  );

  const create = useMutation({
    mutationFn: () =>
      api.post("/payments", {
        direction,
        customerId: direction === "received" ? partyId : undefined,
        vendorId: direction === "made" ? partyId : undefined,
        txnDate,
        amount: appliedTotal,
        depositAccountId,
        method: method || undefined,
        reference: reference || undefined,
        applications: openDocs
          .filter((d) => dollarsToCents(applied[d.id] || "0") > 0)
          .map((d) => ({
            [d.key]: d.id,
            amount: dollarsToCents(applied[d.id]),
          })),
      }),
    onSuccess: onSaved,
  });

  const parties = direction === "received" ? customers ?? [] : vendors ?? [];
  const valid = !pilot.isLoading && partyId && depositAccountId && (!pilot.data?.enabled || bankAccounts.some(a=>a.id===depositAccountId)) && appliedTotal > 0 && (!(invoice || bill) ||
    (openDocs.length === 1 && appliedTotal <= openDocs[0].balance));

  return (
    <Modal open={open} onClose={() => {if (!create.isPending) onClose();}} title="Record payment" wide>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !create.isPending) create.mutate();
        }}
      >
        {invoice && <p className="rounded-md bg-brand-50 p-3 text-sm">Record money already received for {invoice.docNumber ?? "this invoice"}. Choose the deposit account and enter the amount received. This does not charge the customer or send money.</p>}
        {bill && <p className="rounded-md bg-brand-50 p-3 text-sm">Record money already paid for {bill.docNumber ?? "this bill"}. Choose the payment account and enter the amount paid. This records the payment; it does not send money to the vendor.</p>}
        <div className="grid grid-cols-2 gap-4">
          <Field required label="Direction">
            <Select
              disabled={!!(invoice || bill)}
              value={direction}
              onChange={(e) => {
                setDirection(e.target.value as "received" | "made");
                setPartyId("");
                setDepositAccountId("");
                setApplied({});
              }}
            >
              <option value="received">Receive payment (from customer)</option>
              <option value="made">Pay bill (to vendor)</option>
            </Select>
          </Field>
          <Field required label={direction === "received" ? "Customer" : "Vendor"}>
            <Select disabled={!!(invoice || bill)} value={partyId} onChange={(e) => {setPartyId(e.target.value);setApplied({});}}>
              <option value="">— select —</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Field required label={direction === "received" ? "Deposit to" : "Pay from"} hint={pilot.data?.enabled ? (merchantClearingHint(bankAccounts.find(a => a.id === depositAccountId), direction) ?? (direction === 'received' ? 'Choose where the money was received. Use Undeposited Funds for receipts awaiting deposit.' : 'Choose the bank, cash, clearing or credit card account used to pay.')) : undefined}>
            <Select
              value={depositAccountId}
              onChange={(e) => setDepositAccountId(e.target.value)}
            >
              <option value="">— select —</option>
              {bankAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {pilot.data?.enabled ? paymentAccountLabel(a) : a.code + ' ' + a.name}
                </option>
              ))}
            </Select>
          </Field>
          {pilot.data?.enabled && accounts && bankAccounts.length === 0 && <p role="alert">No suitable active account is available in this currency. Review account detail types in Chart of Accounts.</p>}
          <Field required label="Date">
            <TextField
              type="date"
              value={txnDate}
              onChange={(e) => setTxnDate(e.target.value)}
            />
          </Field>
          <Field label="Method">
            <TextField
              value={method}
              placeholder="check, card, ach…"
              onChange={(e) => setMethod(e.target.value)}
            />
          </Field>
        </div>

        {partyId && (
          <div>
            <p className="text-sm text-gray-600">Enter an amount for at least one document (required).</p><p className="label">Apply to open {direction === "received" ? "invoices" : "bills"}</p>
            {openDocs.length === 0 ? (
              <p className="rounded-md bg-gray-50 px-3 py-4 text-sm text-gray-400">
                No open documents for this {direction === "received" ? "customer" : "vendor"}.
              </p>
            ) : (
              <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
                {openDocs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-4 px-3 py-2">
                    <span className="text-sm font-medium">{d.label}</span>
                    <span className="text-sm text-gray-500">
                      open <Money cents={d.balance} currency={currency} />
                    </span>
                    <div className="w-32">
                      <TextField
                        type="number"
                        step="0.01"
                        className="text-right"
                        placeholder="0.00"
                        aria-label={`Amount to apply to ${d.label}`} value={applied[d.id] ?? ""}
                        onChange={(e) =>
                          setApplied({ ...applied, [d.id]: e.target.value })
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-gray-200 pt-3">
          <Field label="Reference">
            <TextField
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </Field>
          <div className="text-right">
            <span className="mr-3 text-sm text-gray-500">Payment total</span>
            <span className="text-xl font-semibold">
              <Money cents={appliedTotal} currency={currency} />
            </span>
          </div>
        </div>

        {(invoice || bill) && openDocs.length === 1 && appliedTotal > openDocs[0].balance &&
          <p role="alert" className="text-sm text-red-700">Enter an amount no greater than the remaining document balance.</p>}
        {create.error && <ErrorState error={create.error} />}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" disabled={create.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!valid || create.isPending}>
            {create.isPending ? "Saving…" : "Record payment"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
